import { DeleteOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { BasketballMessage } from '@hefe/models';
import { BasketballMessagesSlice } from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import {
  Button,
  Col,
  Divider,
  Empty,
  Popconfirm,
  Row,
  Space,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import HtmlParser from '../../../core/utils/html-parser/HtmlParser';
import styles from './messages-detail.component.module.scss';

interface MessagesDetailComponentProps {
  allowDelete: boolean;
  message: BasketballMessage | null;
  mode: 'admin' | 'team' | 'user';
  messageDeleted: () => void;
}

export default function MessagesDetailComponent(
  props: MessagesDetailComponentProps
) {
  const { allowDelete, message } = props;

  const [deleting, setDeleting] = useState(false);

  const dispatch = useAppDispatch();

  if (!message) {
    return <Empty />;
  }

  const senderName = `${message.sender.firstname} ${message.sender.lastname}`;

  const recipients: string[] = [];
  if (message.toCoaches) {
    recipients.push(t('all_coaches'));
  }
  if (message.toReferees) {
    recipients.push(t('all_referees'));
  }
  if (message.toPlayers) {
    recipients.push(t('all_players'));
  }
  for (const team of message.team_recipients) {
    recipients.push(team.name);
  }

  const deleteMessage = () => {
    if (props.message) {
      setDeleting(true);
      dispatch(BasketballMessagesSlice.deleteBasketballMessage(props.message))
        .unwrap()
        .then(() => props.messageDeleted())
        .finally(() => setDeleting(false));
    }
  };

  return (
    <Row gutter={[20, 20]}>
      <Col xs={24}>
        <Row align="middle" justify="space-between">
          <Col span={23}>
            <Space align="start" size={20}>
              <UserAvatar user={message.sender} size="default" />
              <Space direction="vertical" size={0}>
                <Typography.Title level={4} style={{ margin: 0, marginTop: 2 }}>
                  {message.title}
                </Typography.Title>
                <Typography.Text style={{ fontSize: 12 }}>
                  {senderName} | {formatDateTime(message.created_at || '')}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 12 }}>
                  <Space>
                    <Typography.Text>{t('recipients') + ':'}</Typography.Text>
                    {message.own_recipients && (
                      <Typography.Text>
                        {message.own_recipients
                          ?.map((r) => r.firstname)
                          .join(', ')}{' '}
                        |
                      </Typography.Text>
                    )}
                    {recipients.length > 0 && (
                      <Typography.Text>
                        {recipients.join(' | ')}
                      </Typography.Text>
                    )}
                  </Space>
                </Typography.Text>
              </Space>
            </Space>
          </Col>
          {allowDelete && (
            <Col span={1}>
              <Popconfirm
                title={`${t('questions.delete')}`}
                disabled={deleting}
                onConfirm={() => deleteMessage()}
              >
                <Button loading={deleting} icon={<DeleteOutlined />}></Button>
              </Popconfirm>
            </Col>
          )}
        </Row>
        <Divider />
      </Col>
      <Col xs={24}>
        <div className={styles['message-text']}>{HtmlParser(message.text)}</div>
      </Col>
    </Row>
  );
}
